/* eslint-disable no-console */

import { ApolloLink, Observable } from '@apollo/client';
import _some from 'lodash/some';
import _get from 'lodash/get';
import { onError } from '@apollo/client/link/error';
import { getMainDefinition, hasDirectives } from '@apollo/client/utilities';
import { RetryLink } from '@apollo/client/link/retry';
import { bugsnag } from 'lib/external/bugsnag';
import { createLoginUrl } from 'lib/utils/request';
import graphqlLink, { restartSocket } from './graphql';
import restLink from './rest';
import session from './session';
import TokenStore from './TokenStore';

function logErrors(prefix, ...errors) {
  errors.forEach((error) => {
    console.error(prefix, error.message, error);
  });
}

async function notifyBugsnag(prefix, metaData, ...errors) {
  return Promise.all(
    errors.map(async (error) => {
      return bugsnag.notify(new Error(`${prefix}${error.message}`), (event) => {
        event.addMetadata('user', metaData);
      });
    })
  );
}

// `force` also drops a non-'at' access token, so it is reserved for the paths
// with the standing to condemn one.
function logOut(error, apiKey, errMetaData, force) {
  const loginUrl = createLoginUrl();
  session.clear(force);

  if (force && apiKey?.startsWith('op')) {
    notifyBugsnag('[Auth error]: ', errMetaData, error).then(() => {
      window.location = loginUrl;
    });
  } else {
    window.location = loginUrl;
  }

  if (_get(error, 'result.code') === 'SoftAuthenticationError') {
    /* eslint-disable no-alert */
    alert(_get(error, 'result.message', 'There was an error logging in'));
  }
}

// onError does not re-invoke the handler for a forwarded result, so the replay
// has to handle its own failure. A second 401 means the fresh JWT, or the
// attempt to mint one, was rejected too.
function replayWithFreshJwt(
  forward,
  operation,
  networkError,
  apiKey,
  errMetaData
) {
  return new Observable((observer) => {
    let subscription = null;
    let unsubscribed = false;

    // Mint here rather than in authLink, which has no way to decline and would
    // send `Bearer null`.
    session
      .getJwt()
      .catch(() => {
        return null;
      })
      .then((freshJwt) => {
        if (unsubscribed) {
          return;
        }

        if (!freshJwt) {
          // fetchJwt force-clears the access token only on a user-token 401, so
          // a missing key distinguishes a condemned session from a failed mint.
          if (!session.getApiKey()) {
            logOut(networkError, apiKey, errMetaData, true);
          }
          observer.error(networkError);
          return;
        }

        subscription = forward(operation).subscribe({
          next: observer.next.bind(observer),
          complete: observer.complete.bind(observer),
          error: (replayError) => {
            if (replayError?.statusCode === 401) {
              logOut(replayError, apiKey, errMetaData, true);
            }
            observer.error(replayError);
          },
        });
      });

    return () => {
      unsubscribed = true;
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  });
}

/* Without this RetryLink, subscriptions eventually disconnect after many JWT failures */
export const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 3 * 60 * 1000,
    jitter: true,
  },
  attempts: {
    max: Infinity,
    retryIf: (error) => {
      return error.message.includes('JWTExpired');
    },
  },
});

const errorLink = onError(
  ({ forward, graphQLErrors, operation, networkError }) => {
    const userId = new TokenStore('userId').get();
    const userCompany = new TokenStore('company').get();
    const apiKey = new TokenStore('apikey').get();

    const errMetaData = { userId, company: userCompany };

    if (graphQLErrors) {
      logErrors('[GraphQL error]:', ...graphQLErrors);
      notifyBugsnag('[GraphQL error]:', errMetaData, ...graphQLErrors);
    }
    if (networkError) logErrors('[Network error]:', networkError);

    if (networkError) {
      const { statusCode } = networkError;

      // RestLink claims an operation by its @rest directive, and rest's authLink
      // sends the access token itself rather than a JWT.
      const isJwtOperation = !hasDirectives(['rest'], operation.query);

      // Only a JWT-authenticated 401 indicts the JWT alone: user-token is the
      // only endpoint that can condemn the access token, and fetchJwt already
      // force-clears when it answers 401. Subscriptions carry the same stale
      // token, hence restartSocket.
      if (statusCode === 401 && isJwtOperation) {
        restartSocket();
        session.clearJwt();

        return replayWithFreshJwt(
          forward,
          operation,
          networkError,
          apiKey,
          errMetaData
        );
      }

      // A REST 401 rejected the access token, so it is the revoked-device
      // signal. Falling through rather than returning keeps non-idempotent REST
      // mutations on the bail below instead of replaying them.
      if (statusCode === 401 || statusCode === 404) {
        logOut(networkError, apiKey, errMetaData, statusCode === 401);
      }
    }

    const isGraphQLErr = _some(graphQLErrors || [], (err) => {
      const code = err.extensions ? err.extensions.code : null;
      return code === 'invalid-jwt';
    });

    const isSubscriptionErr =
      getMainDefinition(operation.query).operation === 'subscription' &&
      networkError;

    if (isGraphQLErr || isSubscriptionErr) {
      restartSocket();
      session.clearJwt();
    }

    const isMutationErr =
      getMainDefinition(operation.query).operation === 'mutation';

    if (isMutationErr) {
      // bail so failed mutations do not retry by default when passed on to forward
      // https://www.apollographql.com/docs/react/data/error-handling/#on-graphql-errors
      // note that networkError may sometimes be undefined; this seems to be contingent on
      // err statusCode (see https://github.com/machinemetrics/OperatorView/pull/567 for context)
      return null;
    }

    return forward(operation);
  }
);

export { graphqlLink, errorLink };
export default ApolloLink.from([retryLink, errorLink, restLink, graphqlLink]);
